import axios from 'axios'
import { toast } from 'react-toastify'

const API_BASE_URL =  'https://agent-backend.specto.sa/api'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Handle responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    
    const message = error.response?.data?.message || 'An error occurred'
    toast.error(message)
    
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
}

// Users API
export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, passwordData) => api.post(`/users/${id}/reset-password`, passwordData),
  getProfile: () => api.get('/users/profile'),
  updateProfile: (profileData) => api.put('/users/profile', profileData),
}

// Agents API
export const agentsAPI = {
  getAgents: () => api.get('/agents'),
  getAgent: (id) => api.get(`/agents/${id}`),
  createAgent: (agentData) => api.post('/agents', agentData),
  updateAgent: (id, agentData) => api.put(`/agents/${id}`, agentData),
  deleteAgent: (id) => api.delete(`/agents/${id}`),
  syncAgents: () => api.post('/agents/sync'),
  getVoices: () => api.get('/agents/resources/voices'),
  getKnowledgeBases: () => api.get('/agents/resources/knowledge-bases'),
}

// Phone Numbers API
export const phoneNumbersAPI = {
  getPhoneNumbers: () => api.get('/phones'),
  getPhoneNumber: (id) => api.get(`/phones/${id}`),
  createPhoneNumber: (phoneData) => api.post('/phones', phoneData),
  updatePhoneNumber: (id, phoneData) => api.put(`/phones/${id}`, phoneData),
  deletePhoneNumber: (id) => api.delete(`/phones/${id}`),
  syncPhoneNumbers: () => api.post('/phones/sync'),
  getCarriers: () => api.get('/phones/resources/carriers'),
}

// Apps API
export const appsAPI = {
  getApps: () => api.get('/apps'),
  getApp: (id) => api.get(`/apps/${id}`),
  createApp: (appData) => api.post('/apps', appData),
  updateApp: (id, appData) => api.put(`/apps/${id}`, appData),
  deleteApp: (id) => api.delete(`/apps/${id}`),
  syncApps: () => api.post('/apps/sync'),
  testWebhook: (id) => api.post(`/apps/${id}/test-webhook`),
  getSpeechServices: () => api.get('/apps/resources/speech-services'),
}

// Calls API
export const callsAPI = {
  getCalls: (params) => api.get('/calls', { params }),
  getCall: (id) => api.get(`/calls/${id}`),
  syncCalls: (syncData) => api.post('/calls/sync', syncData),
  getCallStats: (params) => api.get('/calls/stats/summary', { params }),
  getCallTimeline: (params) => api.get('/calls/stats/timeline', { params }),
  processCall: (id) => api.post(`/calls/${id}/process`),
  reprocessCall: (id) => api.post(`/calls/${id}/reprocess`),
  getTranscript: (id) => api.get(`/calls/${id}/transcript`),
  getAudioUrl: (id) => `${API_BASE_URL}/calls/${id}/audio`,
}

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  assignResources: (assignmentData) => api.post('/admin/assign-resources', assignmentData),
  getAssignments: () => api.get('/admin/assignments'),
  bulkUserAction: (actionData) => api.post('/admin/bulk-user-action', actionData),
  getSystemHealth: () => api.get('/admin/system-health'),
}

// Tickets API
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.patch(`/tickets/${id}`, data),
  close: (id) => api.post(`/tickets/${id}/close`),
  reopen: (id) => api.post(`/tickets/${id}/reopen`),
}

export default api
